import {ComponentPropsWithoutRef, ReactNode, useState} from 'react';
import {usePlayerStore} from '@common/player/hooks/use-player-store';
import {usePlayerActions} from '@common/player/hooks/use-player-actions';
import {IconButton} from '@ui/buttons/icon-button';
import {loadMediaItemTracks} from '@app/web-player/requests/load-media-item-tracks';
import {trackToMediaItem} from '@app/web-player/tracks/utils/track-to-media-item';
import {PauseIcon} from '@ui/icons/material/Pause';
import {EqualizerImage} from '@app/web-player/tracks/equalizer-image/equalizer-image';
import {PlayArrowFilledIcon} from '@app/web-player/tracks/play-arrow-filled';
import {ButtonColor, ButtonVariant} from '@ui/buttons/get-shared-button-style';
import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {Track} from '@app/web-player/tracks/track';
import {ButtonSize} from '@ui/buttons/button-size';
import { useUserRolesAccess } from '@common/auth/user-roles-access';
import { addTrackFlags, canPlayTrack, createTrackPlayFilter } from '@app/web-player/tracks/utils/track-filter';
import { useAuth } from '@common/auth/use-auth';
import {AppHaptics} from '@common/core/capacitor/app-haptics';
import clsx from 'clsx';

interface PlaybackToggleButtonProps {
  queueId?: string;
  // track that should be cued
  track?: Track;
  // queue should be overwritten with these tracks
  tracks?: Track[];
  label?: ReactNode;
  radius?: string;
  variant?: ButtonVariant;
  color?: ButtonColor;
  disabled?: boolean;
  className?: string;
  buttonType: 'icon' | 'text';
  equalizerColor?: 'white' | 'black';
  size?: ButtonSize;
  onClick?: () => void;
}
export function PlaybackToggleButton({
  queueId,
  track,
  tracks,
  label,
  radius,
  variant,
  color,
  disabled,
  className,
  buttonType,
  equalizerColor = buttonType === 'text' ? 'white' : 'black',
  size,
  onClick
}: PlaybackToggleButtonProps) {
  const [isHover, setIsHover] = useState(false);
  const {user} = useAuth();
  const {userAccess} = useUserRolesAccess();
  const trackPlayFilterOptions = { permissions: userAccess.medias.play, userId: user?.id };
  const playTrackFilter = createTrackPlayFilter(trackPlayFilterOptions);
  const modelIsQueued = usePlayerStore(s => {
    // specified queue ID is cued
    if (s.cuedMedia && queueId && s.cuedMedia.groupId === queueId) {
      return true;
    }
    // specified track is cued
    if (s.cuedMedia && track && s.cuedMedia.meta.id === track.id) {
      return true;
    }
    return false;
  });
  const isPlaying = usePlayerStore(s => s.isPlaying);
  const controlsLocked = usePlayerStore(s => s.controlsLocked);
  const modelIsPlaying = isPlaying && modelIsQueued;
  const player = usePlayerActions();
  const isDisabled = Boolean(disabled || controlsLocked);

  const statusIcon = modelIsPlaying ? (
    isHover ? (
      <PauseIcon />
    ) : (
      <EqualizerImage color={equalizerColor} />
    )
  ) : (
    <PlayArrowFilledIcon />
  );
  
  const sharedProps: ComponentPropsWithoutRef<'button'> = {
    disabled: isDisabled,
    onPointerEnter: () => {
      setIsHover(true);
    },
    onPointerLeave: () => {
      setIsHover(false);
    },
    onClick: async () => {
      if (controlsLocked) {
        return;
      }
      if(onClick) onClick();
      void AppHaptics.impactLight();
      if (modelIsPlaying) {
        player.pause();
      } else if (modelIsQueued) {
        await player.play();
      } else {
        let newQueue: Track[] = [];
        let newIndex: number = 0;
        
        // if (tracks) {
        //   newQueue = [...tracks];
        //   newIndex = track ? tracks.findIndex(t => t.id === track.id) : 0;
        // } else if (track) {
        //   newQueue = [track];
        // } else {
        //   newQueue = await loadMediaItemTracks(queueId!);
        // }
        if (tracks) {
          const filteredTracks = (tracks as Track[])
              .filter(playTrackFilter)
              .map(addTrackFlags);
          newQueue = [...filteredTracks];
          newIndex = track
            ? filteredTracks.findIndex(t => t.id === track.id)
            : 0;
        } else if (track) {
          const shouldInclude = canPlayTrack(track, trackPlayFilterOptions);
          newQueue = shouldInclude ? [track] : [];
        } else {
          const loadedTracks = await loadMediaItemTracks(queueId!);
          const filteredTracks = (loadedTracks as Track[])
              .filter(playTrackFilter)
              .map(addTrackFlags);
          newQueue = filteredTracks;
        }
        
        if (newQueue.length) {
          await player.overrideQueueAndPlay(
            newQueue.map(t => trackToMediaItem(t, queueId)),
            newIndex,
          );
        }
      }
    },
  };
  
  if (buttonType === 'icon') {
    return (
      <IconButton
        {...sharedProps}
        variant={variant}
        color={color}
        radius={radius}
        size={size}
        className={clsx(className, isDisabled && 'cursor-default opacity-50')}
      >
        {statusIcon}
      </IconButton>
    );
  }

  return (
    <Button
      {...sharedProps}
      variant={variant || 'flat'}
      color={color || 'primary'}
      radius={radius || 'rounded-full'}
      startIcon={statusIcon}
      size={size}
      className={clsx(className, isDisabled && 'cursor-default opacity-50')}
    >
      {label ?? (modelIsPlaying ? <Trans message="Pause" /> : <Trans message="Play" />)}
    </Button>
  );
}
